import { Injectable } from '@angular/core';

import { AccaBoostConfig, BetslipConfig, EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { LocalStoreService } from '@frontend/vanilla/core';
import { Store } from '@ngrx/store';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { getLegsCount } from 'packages/sports/common/betslip/core/utils';
import { Observable, map } from 'rxjs';

import { getComboSlipId } from '../../../bet-generation/services/slip.utils';
import LinearBetslipMessagesService from '../../../linear/linear-betslip-messages.service';
import { InfoTrackingContext } from '../../../tracking/models';
import BetslipComboBetActions from '../../actions';
import { LinearComboBetTracking, LinearComboExpandedLocalStorageKey } from '../../models';
import { selectMainComboContainerState } from '../../selectors';
import { ILinearComboBetContainerService, LinearComboBetContainerViewModel, SgpBadgeType } from './linear-combo-bet-container-service.interface';

@Injectable()
export class LinearMainComboContainerService implements ILinearComboBetContainerService {
    constructor(
        private store: Store,
        private linearMessagesService: LinearBetslipMessagesService,
        private localStorage: LocalStoreService,
        private siteCore: Sitecore,
        private accaBoostConfig: AccaBoostConfig,
        private epcotConfig: EpcotConfig,
        private betslipConfig: BetslipConfig,
    ) {
        const isPickAreaExpandable = this.betslipConfig.linearModeCheckboxesEnabled;
        const isEpcotEnabled = this.epcotConfig.isEnabled;
        const betslipType = BetslipType.Combo;
        const tokenContext = { betslipType };

        this.viewModel$ = this.store.select(selectMainComboContainerState(tokenContext)).pipe(
            map(
                ({
                    comboPicks,
                    comboEachWay,
                    hasEachWayPick,
                    isRewardSelectorVisible,
                    betBuilderPicksCount,
                    moduleErrors,
                    placeablePicks,
                    placeableBetBuilderPicksCount,
                    hasComboPrevention,
                    isAccaBoostToken,
                }) => {
                    const picks = Object.values(comboPicks);
                    const placeablePicksCount = placeablePicks.length;
                    const legsCount = getLegsCount(placeablePicks);

                    const hasBetBuilderPicks = betBuilderPicksCount > 0;
                    const { infoHeaderTitle, infoHeader, infoMessage } = this.getMessageInformation(isEpcotEnabled, hasBetBuilderPicks, legsCount);

                    return {
                        errors: moduleErrors,
                        hasComboPrevention,
                        multiTitle: this.getMultiTitle(legsCount, hasBetBuilderPicks, placeableBetBuilderPicksCount > 0, isEpcotEnabled),
                        multiTitleBadge: betslipConfig.isSgpBadgeInTitleEnabled && hasBetBuilderPicks ? SgpBadgeType.SgpPlus : undefined,
                        isPickAreaExpandable,
                        isEpcotEnabled,
                        isEachWay: comboEachWay,
                        isErrorVisible: hasComboPrevention || moduleErrors.length > 0,
                        placeablePicksCount,
                        totalPicksCount: picks.length,
                        showEachWayToggle: hasEachWayPick,
                        tokenContext,
                        isRewardSelectorVisible,
                        betslipType,
                        slipId: getComboSlipId(),
                        showRemoveButton: false,
                        subTitle: this.getSubTitle(placeablePicksCount, placeableBetBuilderPicksCount, legsCount, isEpcotEnabled),
                        showInfoIcon: isEpcotEnabled && (hasBetBuilderPicks || !!infoMessage),
                        showDetailsSection: hasBetBuilderPicks && !isPickAreaExpandable,
                        showSelectionCounter: !isEpcotEnabled,
                        infoMessage,
                        infoHeaderTitle,
                        infoHeader,
                        infoTrackingContext: hasBetBuilderPicks ? InfoTrackingContext.SgpPlus : InfoTrackingContext.ComboBet,
                        isTotalOddsIndicatorEnabled: false,
                        showTokenSummaryUnderTitle: this.accaBoostConfig.isEnhancedLayoutEnabled && isAccaBoostToken,
                    };
                },
            ),
        );
    }

    readonly isExpandedByDefault: boolean = this.localStorage.get(LinearComboExpandedLocalStorageKey) || false;

    readonly viewModel$: Observable<LinearComboBetContainerViewModel>;

    remove(): void {}

    toggleEachWay(isEachWay: boolean): void {
        this.store.dispatch(BetslipComboBetActions.setComboEachWay({ isEachWay }));
    }

    toggleExpansion(isExpanded: boolean): void {
        this.store.dispatch(
            BetslipComboBetActions.linearPickListVisibilitySwitch({
                positionEvent: LinearComboBetTracking.ParlayPositionEvent,
                eventDetails: isExpanded ? LinearComboBetTracking.ViewPickListEventDetails : LinearComboBetTracking.HidePickListEventDetails,
            }),
        );

        this.localStorage.set(LinearComboExpandedLocalStorageKey, isExpanded);
    }

    private getMultiTitle(legsCount: number, hasBetBuilderPicks: boolean, hasBetBuilderPicksSelected: boolean, isEpcotEnabled: boolean): string {
        return this.linearMessagesService.getComboSectionTitle(isEpcotEnabled, legsCount, hasBetBuilderPicks, hasBetBuilderPicksSelected);
    }

    private getSubTitle(
        selectedPicksCount: number,
        selectedBetBuilderPicksCount: number,
        legsCount: number,
        isEpcotEnabled: boolean,
    ): string | undefined {
        return isEpcotEnabled
            ? this.linearMessagesService.getComboBetBuilderPicksDescription(
                  selectedPicksCount - selectedBetBuilderPicksCount,
                  selectedBetBuilderPicksCount,
              )
            : this.linearMessagesService.getComboBetBuilderEventLegsDescription(selectedPicksCount, legsCount, selectedBetBuilderPicksCount > 0);
    }

    private getMessageInformation(
        isEpcotEnabled: boolean,
        hasBetBuilderPicks: boolean,
        legsCount: number,
    ): { infoHeaderTitle: string; infoHeader: string; infoMessage: string } {
        if (!isEpcotEnabled) {
            return {
                infoHeaderTitle: '',
                infoHeader: '',
                infoMessage: '',
            };
        }

        if (hasBetBuilderPicks) {
            return {
                infoHeaderTitle: this.siteCore.betslip.messages.BetbuilderComboHeaderTitle,
                infoHeader: this.siteCore.betslip.messages.BetbuilderComboInfoHeader,
                infoMessage: this.siteCore.betslip.messages.BetbuilderComboInfoText,
            };
        }

        if (legsCount > 1) {
            return {
                infoHeaderTitle: this.siteCore.betslip.linear.ParlayInfoHeaderTitle,
                infoHeader: this.siteCore.betslip.linear.ParlayInfoHeader,
                infoMessage: interpolateDoubleBracesString(this.siteCore.betslip.linear.ParlayInfoMessage, { count: legsCount }),
            };
        }

        return {
            infoHeaderTitle: '',
            infoHeader: '',
            infoMessage: '',
        };
    }
}
